import { useCallback } from 'react';
import DOMPurify from 'dompurify';
import { EmailAttachment, EmailMessage } from '../../../models/Email';
import { EmailContent, EmailContentAttachment } from '../../../services/fetchEmailContent';
import { normalizeSubject } from '../../../utils/emailContactHelpers';
import { useEmailSanitizationLogger } from './useEmailSanitizationLogger';

function resolveAttachments(attachments: EmailContentAttachment[] | undefined): {
  attachments: EmailAttachment[];
  hasRejectedAttachments: boolean;
} {
  const list = attachments ?? [];

  if (list.some(a => a.scanStatus === 'rejected')) {
    return { attachments: [], hasRejectedAttachments: true };
  }

  return {
    attachments: list.map(a => ({ fileId: a.fileId, fileName: a.fileName })),
    hasRejectedAttachments: false
  };
}

export function useEnrichEmailWithContent(): (message: EmailMessage, content: EmailContent) => EmailMessage {
  const { logSanitization } = useEmailSanitizationLogger();

  const sanitizeBodyHtml = useCallback(
    (rawBodyHtml: string | null | undefined, contactId: string): string | undefined => {
      if (!rawBodyHtml) return undefined;

      logSanitization({ contactId, outcome: 'attempted' });

      try {
        const sanitizedHtml = DOMPurify.sanitize(rawBodyHtml);
        logSanitization({ contactId, outcome: sanitizedHtml !== rawBodyHtml ? 'modified' : 'unchanged' });
        return sanitizedHtml;
      } catch (error) {
        logSanitization({ contactId, outcome: 'failed', error });
        return undefined;
      }
    },
    [logSanitization]
  );

  return useCallback(
    (message: EmailMessage, content: EmailContent): EmailMessage => {
      const toRecipients = [
        ...(content.deliveredTo ? [{ emailAddress: content.deliveredTo }] : []),
        ...(content.to ?? []).map(emailAddress => ({ emailAddress }))
      ];

      const { attachments, hasRejectedAttachments } = resolveAttachments(content.attachments);

      return {
        ...message,
        subject: normalizeSubject(content.subject),
        from: content.from ? { emailAddress: content.from } : undefined,
        to: toRecipients,
        cc: (content.cc ?? []).map(emailAddress => ({ emailAddress })),
        otherRecipients: (content.otherRecipients ?? []).map(emailAddress => ({ emailAddress })),
        bodyHtml: sanitizeBodyHtml(content.bodyHtml, message.contactId),
        attachments,
        hasRejectedAttachments,
        contentLoaded: true
      };
    },
    [sanitizeBodyHtml]
  );
}
